import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, DatePicker, Select, TextInput } from 'components/Form'
import {
    createApiRecord,
    updateApiRecord,
} from 'services/CompanyConfigurationService'
import { useDispatch, useSelector } from 'react-redux'
import { getRecord } from './store/dataSlice'
import reducer from './store'
import { injectReducer } from 'store'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { Success } from 'utils/alerts'
import dayjs from 'dayjs'
injectReducer('companyConfiguration', reducer)

const validationSchema = Yup.object().shape({
    company_id: Yup.number().required('Please select company'),
    free_users: Yup.number().required('Free users Required'),
    monthly_limit: Yup.number().required('Monthly limit Required'),
    monthly_limit_per_user: Yup.number().required(
        'Monthly limit per user Required',
    ),
    start_date: Yup.date().required('Start date Required'),
    end_date: Yup.date().required('End date Required'),
})

const Create = () => {
    const { id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { companyData } = useSelector((state) => state.listData)
    const { view, record, loading } = useSelector(
        (state) => state.companyConfiguration.data,
    )

    useEffect(() => {
        if (id) {
            fetchData(id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const fetchData = (id) => dispatch(getRecord(id))

    const handleDiscard = () =>
        navigate('/company-management/company-configuration')

    const handleSubmit = (values, setSubmitting) => {
        const data = {
            ...values,
            start_date: dayjs(values.start_date).format('YYYY-MM-DD'),
            end_date: dayjs(values.end_date).format('YYYY-MM-DD'),
        }
        setSubmitting(true)
        if (id) {
            if (typeof data?.company_logo === 'string') {
                delete data?.company_logo
            }
            updateApiRecord(id, data)
                .then(() => {
                    Success('Updated Successfully!')
                    handleDiscard()
                    // dispatch(getCompanyData())
                    setSubmitting(false)
                })
                .catch((error) => {
                    setSubmitting(false)
                    return error
                })
        } else {
            createApiRecord(data)
                .then(() => {
                    Success('Created Successfully!')
                    handleDiscard()
                    // dispatch(getCompanyData())
                    setSubmitting(false)
                })
                .catch((error) => {
                    setSubmitting(false)
                    return error
                })
        }
    }

    if (loading) {
        return null
    }

    return (
        <>
            <div className="flex flex-col gap-1 mb-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">
                    Company Configuration
                </h2>
            </div>
            <div className="flex flex-col items-center">
                <div className="w-full bg-white border rounded-lg border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                    <div className="px-5 py-3 border-b border-stroke dark:border-strokedark">
                        <h3 className="font-medium text-black dark:text-white">
                            {id
                                ? 'Update Company Configuration'
                                : 'Add New Company Configuration'}
                        </h3>
                    </div>
                    <div className="flex flex-col gap-3 p-6">
                        <Formik
                            initialValues={id ? record : view}
                            validationSchema={validationSchema}
                            onSubmit={(values, { setSubmitting }) => {
                                handleSubmit(values, setSubmitting)
                            }}
                        >
                            {({ values, touched, errors, isSubmitting }) => (
                                <Form>
                                    <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-6">
                                        <Field name="company_id">
                                            {({ field, form }) => (
                                                <Select
                                                    options={companyData}
                                                    label="Company"
                                                    size="sm"
                                                    placeholder="Select"
                                                    name="company_id"
                                                    className="md:col-span-2"
                                                    error={
                                                        errors.company_id &&
                                                        touched.company_id
                                                    }
                                                    message={errors.company_id}
                                                    value={values?.company_id}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="free_users">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Free Users"
                                                    name="free_users"
                                                    error={
                                                        errors.free_users &&
                                                        touched.free_users
                                                    }
                                                    message={errors.free_users}
                                                    value={values?.free_users}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="monthly_limit">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Monthly Limit"
                                                    name="monthly_limit"
                                                    error={
                                                        errors.monthly_limit &&
                                                        touched.monthly_limit
                                                    }
                                                    message={
                                                        errors.monthly_limit
                                                    }
                                                    value={
                                                        values?.monthly_limit
                                                    }
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="monthly_limit_per_user">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Monthly Limit Per User"
                                                    name="monthly_limit_per_user"
                                                    error={
                                                        errors.monthly_limit_per_user &&
                                                        touched.monthly_limit_per_user
                                                    }
                                                    message={
                                                        errors.monthly_limit_per_user
                                                    }
                                                    value={
                                                        values?.monthly_limit_per_user
                                                    }
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="start_date">
                                            {({ field, form }) => (
                                                <DatePicker
                                                    className="md:col-span-2"
                                                    label="Start Date"
                                                    name="start_date"
                                                    error={
                                                        errors.start_date &&
                                                        touched.start_date
                                                    }
                                                    message={errors.start_date}
                                                    value={
                                                        values?.start_date
                                                            ? dayjs(
                                                                  values?.start_date,
                                                              ).format(
                                                                  'YYYY-MM-DD',
                                                              )
                                                            : ''
                                                    }
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="end_date">
                                            {({ field, form }) => (
                                                <DatePicker
                                                    className="md:col-span-2"
                                                    label="End Date"
                                                    name="end_date"
                                                    error={
                                                        errors.end_date &&
                                                        touched.end_date
                                                    }
                                                    message={errors.end_date}
                                                    value={
                                                        values?.end_date
                                                            ? dayjs(
                                                                  values?.end_date,
                                                              ).format(
                                                                  'YYYY-MM-DD',
                                                              )
                                                            : ''
                                                    }
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                    </div>
                                    <div className="flex items-center justify-end gap-4 mt-3">
                                        <Button
                                            className="h-10 w-18 text-primary border border-primary dark:text-secondary dark:border-secondary"
                                            onClick={handleDiscard}
                                        >
                                            Discard
                                        </Button>
                                        <Button
                                            type="submit"
                                            className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-primary bg-primary dark:border-secondary dark:bg-secondary"
                                            disabled={isSubmitting}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Create
